import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useOrgData } from '../../contexts/OrgDataContext';
import { useLocale } from '../../contexts/LocaleContext';
import './Controls.css';

// Progress Modal Component for PDF AI Generation
const ProgressModal: React.FC<{ isVisible: boolean; elapsedSeconds: number }> = ({ isVisible, elapsedSeconds }) => {
  const { t, language } = useLocale();

  if (!isVisible) return null;

  const progress = Math.min((elapsedSeconds / 300) * 100, 95);
  const minutes = Math.floor(elapsedSeconds / 60);
  const seconds = elapsedSeconds % 60;

  const timeDisplay = language === 'en'
    ? `${minutes > 0 ? `${minutes}m ` : ''}${seconds}s`
    : language === 'ar'
    ? `${minutes > 0 ? `${minutes} دقيقة و` : ''}${seconds} ثانية`
    : `${minutes > 0 ? `${minutes} דקות ו-` : ''}${seconds} שניות`;

  return (
    <div className="progress-modal-overlay">
      <div className="progress-modal">
        <div className="progress-modal-icon">🤖</div>
        <h3>{t('msg.processingPdf')}</h3>
        <p>{language === 'en' ? 'Please wait, this can take up to 5 minutes' : language === 'ar' ? 'يرجى الانتظار، قد يستغرق هذا حتى 5 دقائق' : 'אנא המתן, התהליך יכול לקחת עד 5 דקות'}</p>
        <div className="progress-bar-container">
          <div className="progress-bar" style={{ width: `${progress}%` }}></div>
        </div>
        <div className="progress-time">{timeDisplay}</div>
        <div className="progress-steps">
          <div className={`progress-step ${elapsedSeconds >= 0 ? 'active' : ''}`}>📄 {language === 'en' ? 'Reading PDF' : language === 'ar' ? 'قراءة PDF' : 'קריאת PDF'}</div>
          <div className={`progress-step ${elapsedSeconds >= 5 ? 'active' : ''}`}>🔍 {t('msg.analyzingStructure')}</div>
          <div className={`progress-step ${elapsedSeconds >= 30 ? 'active' : ''}`}>📊 {t('msg.extractingData')}</div>
          <div className={`progress-step ${elapsedSeconds >= 60 ? 'active' : ''}`}>✨ {t('msg.buildingOrgBoard')}</div>
        </div>
      </div>
    </div>
  );
};

// Dropdown Menu Component
const DropdownMenu: React.FC<{
  label: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}> = ({ label, icon, children, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`dropdown-menu ${className}`} ref={menuRef}>
      <button className="btn dropdown-trigger" onClick={() => setIsOpen(!isOpen)}>
        {icon}
        {label}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '4px' }}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {isOpen && (
        <div className="dropdown-content" onClick={() => setIsOpen(false)}>
          {children}
        </div>
      )}
    </div>
  );
};

interface ControlsProps {
  onExportPng: () => void;
  onExportSvg: () => void;
  onPrint: () => void;
  zoom: number;
  setZoom: (value: number) => void;
  onZoomReset: () => void;
  textSize: number;
  setTextSize: (value: number) => void;
  onTextSizeReset: () => void;
  divisionWidth: number;
  setDivisionWidth: (value: number) => void;
  onDivisionWidthReset: () => void;
  onOpenExecutiveModal: () => void;
  canEdit?: boolean;
}

export const Controls: React.FC<ControlsProps> = ({
  onExportPng,
  onExportSvg,
  onPrint,
  zoom,
  setZoom,
  onZoomReset,
  textSize,
  setTextSize,
  onTextSizeReset,
  divisionWidth,
  setDivisionWidth,
  onDivisionWidthReset,
  onOpenExecutiveModal,
  canEdit = true
}) => {
  const {
    exportToJson,
    importFromJson,
    generateFromPdf,
    divisions,
    focusedDivision,
    setFocusedDivision,
    focusedDepartment,
    setFocusedDepartment
  } = useOrgData();
  const { t, language } = useLocale();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Timer effect for progress tracking
  useEffect(() => {
    if (isGenerating) {
      setElapsedSeconds(0);
      timerRef.current = setInterval(() => {
        setElapsedSeconds(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isGenerating]);

  const handleDivisionFilterChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setFocusedDivision(value === 'all' ? null : value);
  }, [setFocusedDivision]);

  const handleDepartmentFilterChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setFocusedDepartment(value === 'all' ? null : parseInt(value, 10));
  }, [setFocusedDepartment]);

  // Get sorted divisions for the dropdown
  const sortedDivisions = Object.entries(divisions)
    .sort((a, b) => a[1].number - b[1].number);

  // Get departments for the focused division
  const focusedDivisionData = focusedDivision ? divisions[focusedDivision] : null;
  const departments = focusedDivisionData?.departments || [];

  const handleImportClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const success = await importFromJson(file);
      if (success) {
        alert(t('msg.loadSuccess'));
      } else {
        alert(t('msg.loadError'));
      }
      e.target.value = '';
    }
  }, [importFromJson, t]);

  const handlePdfUploadClick = useCallback(() => {
    const confirmMessage = language === 'en'
      ? `Create Org Board from PDF\n\nThe file should contain:\n• 7 Divisions\n• 21 Departments (3 per division)\n• Roles and goals for each department\n• Valuable Final Products (VFP)\n\nContinue?`
      : language === 'ar'
      ? `إنشاء مخطط التنظيم من PDF\n\nيجب أن يحتوي الملف على:\n• 7 شعب\n• 21 قسم (3 لكل شعبة)\n• الأدوار والأهداف لكل قسم\n• المنتجات النهائية القيّمة (VFP)\n\nاستمرار؟`
      : `יצירת לוח התארגנות מקובץ PDF\n\nהקובץ צריך להכיל:\n• 7 חטיבות\n• 21 מחלקות (3 לכל חטיבה)\n• תפקידים ויעדים לכל מחלקה\n• מוצרים סופיים בעלי ערך (VFP)\n\nלהמשיך?`;

    const confirmed = window.confirm(confirmMessage);
    if (confirmed) {
      pdfInputRef.current?.click();
    }
  }, [language]);

  const handlePdfChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setIsGenerating(true);
      try {
        const success = await generateFromPdf(file);
        if (success) {
          alert(t('msg.createSuccess'));
        } else {
          alert(t('msg.createError'));
        }
      } catch (error) {
        alert(t('msg.createError') + ': ' + (error as Error).message);
      } finally {
        setIsGenerating(false);
        e.target.value = '';
      }
    }
  }, [generateFromPdf, t]);

  return (
    <div className="controls">
      {/* Department Filter - only shown when division is selected (placed first for row-reverse) */}
      {focusedDivision && departments.length > 0 && (
        <select
          className="department-filter-select mobile-hide"
          value={focusedDepartment !== null ? focusedDepartment.toString() : 'all'}
          onChange={handleDepartmentFilterChange}
          title={t('controls.filterByDepartment')}
        >
          <option value="all">{t('controls.allDepartments')}</option>
          {departments.map((dept, index) => (
            <option key={index} value={index.toString()}>
              {dept.number}. {dept.name}
            </option>
          ))}
        </select>
      )}

      {/* Division Filter - hidden on mobile (filter is in header) */}
      <select
        className="division-filter-select mobile-hide"
        value={focusedDivision || 'all'}
        onChange={handleDivisionFilterChange}
        title={t('controls.filterByDivision')}
      >
        <option value="all">{t('controls.allDivisions')}</option>
        {sortedDivisions.map(([key, div]) => (
          <option key={key} value={key}>
            {div.icon} {div.number}. {div.name}
          </option>
        ))}
      </select>

      {canEdit && (
        <>
          <div className="controls-separator mobile-hide" />

          {/* Export Dropdown */}
          <DropdownMenu
        label={t('controls.export')}
        className="export-dropdown"
        icon={
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
          </svg>
        }
      >
        <button className="dropdown-item" onClick={onPrint}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
            <rect x="6" y="14" width="12" height="8" />
          </svg>
          {t('controls.print')}
        </button>
        <button className="dropdown-item" onClick={onExportPng}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21,15 16,10 5,21" />
          </svg>
          {t('controls.image')}
        </button>
        <button className="dropdown-item" onClick={onExportSvg}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polygon points="12,2 2,7 12,12 22,7" />
            <polyline points="2,17 12,22 22,17" />
            <polyline points="2,12 12,17 22,12" />
          </svg>
          {t('controls.vector')}
        </button>
        <div className="dropdown-divider" />
        <button className="dropdown-item" onClick={exportToJson}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14,2 14,8 20,8" />
          </svg>
          {t('controls.exportJson')}
        </button>
        <button className="dropdown-item" onClick={handleImportClick}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
          </svg>
          {t('controls.importJson')}
        </button>
      </DropdownMenu>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </>
      )}
    </div>
  );
};
